import type {
  AdConfig,
  AdSet,
  BudgetMode,
  CampaignConfig,
  MetaAdPublishStatus,
} from '../types/metaAdDraft.js';
import { migrateAdditionalAds, migrateCreativeShape } from './metaAdCreativeShape.js';
import { migrateObjectiveShape } from './metaAdObjectiveShape.js';
import { migrateTargetingShape } from './metaAdTargetingShape.js';
import { migratePlacements } from './placementMatrix.js';

// Multi-ad-set Phase 1 — the ONE lossless read-time migration from the legacy
// single-ad-set draft (campaign.budgetLevel + a singular `adSet` + a singular
// `ad`) to the multi-ad-set shape (campaign owns the campaign-wide-locked config,
// `adSets[]` carry per-set targeting/budget/dates, `ads` is the shared creative).
//
// Applied at the SAME three boundaries as the existing shape migrations
// (migrateCreativeShape / migrateTargetingShape / migrateObjectiveShape): the
// server read path (normalizeStoredDraft), the zod request preprocess, and the
// client draftToInput. Idempotent — re-running on an already-multi-ad-set draft is
// a no-op (stable ids preserved). Pure: no I/O, no ambient Date/random.
//
// LEGACY MAPPING (lossless): a legacy draft's single `adSet` becomes adSets[0];
// its campaign-wide fields (optimizationGoal / customEventType / destinationType /
// facebookPage* / placements) lift onto `campaign`; its schedule lifts onto the
// campaign DEFAULT dates (the migrated ad set inherits them, datesOverridden:
// false); budgetLevel → budgetMode; its `ad` becomes the shared `ads`. The result
// is a valid 1-ad-set campaign with no data loss.

// Stable id for the single migrated/legacy ad set (a constant so re-reads don't
// churn the id → autosave loops / React key thrash). Per-draft-local, so a shared
// constant across drafts is fine.
export const LEGACY_AD_SET_ID = 'adset-1';

function budgetModeFrom(campaign: unknown): BudgetMode {
  const c = (campaign ?? {}) as Record<string, unknown>;
  if (c.budgetMode === 'CBO' || c.budgetMode === 'ABO') return c.budgetMode;
  // Legacy budgetLevel: 'campaign' = CBO, anything else (incl. 'adset'/missing) = ABO.
  return c.budgetLevel === 'campaign' ? 'CBO' : 'ABO';
}

// publishStatus default = ACTIVE for any draft (new or legacy) that predates the
// field, preserving the historical no-status-field behaviour (Meta defaults new
// objects to active). In STRICT (zod) mode an unrecognised present value passes
// through so the zod enum rejects a bad write; in coerce mode it's normalised to
// the ACTIVE default so a legacy draft always opens.
function publishStatusFrom(campaign: Record<string, unknown>, strict: boolean): unknown {
  const v = campaign.publishStatus;
  if (v === 'ACTIVE' || v === 'PAUSED') return v;
  if (strict && v !== undefined && v !== null) return v; // let zod reject an invalid authored value
  return 'ACTIVE' satisfies MetaAdPublishStatus;
}

function asDestination(v: unknown): 'instant_form' | 'website' {
  return v === 'website' ? 'website' : 'instant_form';
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

// Build one new-shape AdSet from a new-shape stored ad set (idempotent normalize).
function normalizeAdSet(a: unknown, index: number): AdSet {
  const s = (a ?? {}) as Record<string, unknown>;
  const id = typeof s.id === 'string' && s.id.trim() !== '' ? s.id : `adset-${index + 1}`;
  return {
    id,
    name: typeof s.name === 'string' ? s.name : '',
    targeting: migrateTargetingShape(s.targeting),
    budget: (s.budget ?? null) as AdSet['budget'],
    datesOverridden: s.datesOverridden === true,
    startDate: asIso(s.startDate),
    endDate: asIso(s.endDate),
    // Per-ad-set Active/Off — default ACTIVE (legacy/migrated ad sets that predate
    // the field). A saved value is preserved.
    status: s.status === 'PAUSED' ? 'PAUSED' : 'ACTIVE',
    publishState: (s.publishState ?? null) as AdSet['publishState'],
  };
}

function asIso(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

// Build the single AdSet from a LEGACY singular `adSet`. Its schedule was lifted to
// the campaign default, so the migrated ad set inherits (datesOverridden: false).
function legacyAdSetToAdSet(a: unknown): AdSet {
  const s = (a ?? {}) as Record<string, unknown>;
  return {
    id: LEGACY_AD_SET_ID,
    name: typeof s.name === 'string' ? s.name : '',
    targeting: migrateTargetingShape(s.targeting),
    budget: (s.budget ?? null) as AdSet['budget'],
    datesOverridden: false,
    startDate: null,
    endDate: null,
    status: s.status === 'PAUSED' ? 'PAUSED' : 'ACTIVE',
    publishState: null,
  };
}

// Normalize the shared creative `ads` (handles both the new `ads` and the legacy
// `ad`, folding the legacy ad.storyCreativeImage into creative.storyAsset). In
// strict mode the additional ads pass through VERBATIM (no assetType coercion) so
// a bad value reaches — and is rejected by — the strict zod enum; in coerce mode
// (read paths) migrateAdditionalAds defaults a legacy entry's assetType to 'image'.
function normalizeAds(ad: unknown, strict: boolean): AdConfig {
  const a = (ad ?? {}) as Record<string, unknown>;
  const { storyCreativeImage, ...rest } = a;
  return {
    ...(rest as Omit<AdConfig, 'name' | 'creative' | 'additionalAds' | 'status'>),
    name: typeof a.name === 'string' ? a.name : '',
    // Per-ad Active/Off — default ACTIVE (legacy/migrated primary ads that predate the
    // field; preserve current behaviour). Only an explicit 'PAUSED' is preserved.
    status: a.status === 'PAUSED' ? 'PAUSED' : 'ACTIVE',
    creative: migrateCreativeShape(a.creative, storyCreativeImage),
    additionalAds: strict
      ? ((a.additionalAds ?? []) as AdConfig['additionalAds'])
      : migrateAdditionalAds(a.additionalAds as AdConfig['additionalAds']),
  };
}

// Split coercion from restructuring (see options):
// - COERCE mode (default; READ paths — server normalizeStoredDraft, client
//   draftToInput): an unknown enum VALUE on a legacy draft is coerced to a sane
//   default (objective→LEADS, budgetLevel→ABO, additional-ad assetType→image) so
//   the draft opens rather than hard-failing.
// - STRICT mode (`{ strict: true }`; INPUT VALIDATION — the zod body preprocess):
//   the legacy SHAPE is still restructured (singular adSet/ad → adSets[]/ads,
//   locked fields lifted, the KNOWN legacy goal/budgetLevel mapped when the new
//   field is absent), but an unknown enum VALUE is passed THROUGH so the strict
//   zod enum rejects it loudly instead of silently coercing a bad write.
export function migrateDraftShape(
  draft: unknown,
  opts: { strict?: boolean } = {},
): {
  campaign: CampaignConfig;
  adSets: AdSet[];
  ads: AdConfig;
} {
  const strict = opts.strict === true;
  const d = (draft ?? {}) as Record<string, unknown>;
  const hasNew = Array.isArray(d.adSets);
  const rawCampaign = (d.campaign ?? {}) as Record<string, unknown>;
  const legacyAdSet = (d.adSet ?? {}) as Record<string, unknown>;

  // The objective/optimization/destination fields live on the campaign in the new
  // shape, on the ad set in the legacy shape.
  const optSource = hasNew ? rawCampaign : legacyAdSet;
  // The remaining campaign-wide-locked fields (page, placements) + the default
  // schedule: from the campaign (new) or lifted from the single ad set (legacy).
  const lockedSource = hasNew ? rawCampaign : legacyAdSet;

  let objective: unknown;
  let optimizationGoal: unknown;
  let customEventType: unknown;
  let destinationType: unknown;
  let budgetMode: unknown;
  if (strict) {
    // Trust the authored values; map ONLY the known legacy fields when the new
    // field is absent. An unknown value passes through for zod to reject.
    objective =
      typeof rawCampaign.objective === 'string'
        ? rawCampaign.objective
        : rawCampaign.goal === 'traffic'
          ? 'TRAFFIC'
          : rawCampaign.goal === 'leads'
            ? 'LEADS'
            : rawCampaign.objective;
    budgetMode =
      typeof rawCampaign.budgetMode === 'string'
        ? rawCampaign.budgetMode
        : rawCampaign.budgetLevel === 'campaign'
          ? 'CBO'
          : rawCampaign.budgetLevel === 'adset'
            ? 'ABO'
            : rawCampaign.budgetMode;
    destinationType = optSource.destinationType;
    optimizationGoal = typeof optSource.optimizationGoal === 'string' ? optSource.optimizationGoal : '';
    customEventType =
      typeof optSource.customEventType === 'string' && optSource.customEventType !== ''
        ? optSource.customEventType
        : null;
  } else {
    const obj = migrateObjectiveShape(rawCampaign, optSource);
    objective = obj.objective;
    optimizationGoal = obj.optimizationGoal;
    customEventType = obj.customEventType;
    destinationType = asDestination(optSource.destinationType);
    budgetMode = budgetModeFrom(rawCampaign);
  }

  // Cast through unknown: in strict mode the enum VALUES may be invalid by design
  // (the zod boundary rejects them); in coerce mode they are always valid.
  const campaign = {
    objective,
    budgetMode,
    budget: rawCampaign.budget ?? null,
    publishStatus: publishStatusFrom(rawCampaign, strict),
    optimizationGoal,
    customEventType,
    destinationType,
    facebookPageId: asString(lockedSource.facebookPageId),
    facebookPageName: asString(lockedSource.facebookPageName),
    placements: migratePlacements(lockedSource.placements),
    defaultStartDate: hasNew ? asIso(rawCampaign.defaultStartDate) : asIso(legacyAdSet.startDate),
    defaultEndDate: hasNew ? asIso(rawCampaign.defaultEndDate) : asIso(legacyAdSet.endDate),
    // Timezone override (B) — default false (account tz). Legacy drafts that predate
    // the field read as false (account-tz scheduling, the historical behaviour).
    scheduleUseAustralianEastern: rawCampaign.scheduleUseAustralianEastern === true,
  } as unknown as CampaignConfig;

  const adSets: AdSet[] = hasNew
    ? (d.adSets as unknown[]).map((a, i) => normalizeAdSet(a, i))
    : [legacyAdSetToAdSet(legacyAdSet)];

  const ads = normalizeAds(hasNew ? d.ads : d.ad, strict);

  return { campaign, adSets, ads };
}
